import React, { useContext, useEffect, useState } from "react";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Select,
    Textarea,
} from "@chakra-ui/react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type Props = {
    getFaqs: (currentP?: number) => Promise<void>;
    setIsCreateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    editData: any;
    onSubmit: (editedData: any) => Promise<void>;
};

export default function CreateFaq({
    getFaqs,
    setIsCreateModalOpen,
    editData,
    onSubmit,
}: Props): JSX.Element {
    // console.log("editData", editData)
    const [formData, setFormData] = useState<any>({
        question: "",
        answer: "",
        categoryId: "",
        order: "",
        isActive: false,
    });

    const [categories, setCategories] = useState<{ _id: string; name: string }[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const { toastNotify } = useContext(AppContext);

    // fetch categories
    const getCategories = async () => {
        try {
            setLoadingCategories(true);
            const request: any = {
                limit: 100, // optional, depends on API
                page: 1,
                isActive: true,
            };
            const response: any = await appRequest("faqCategory", "getAll", request);

            if (response.status === "success") {
                setCategories(response?.data?.items || []);
            } else {
                toastNotify({
                    title: "Error",
                    message: response.message || "Failed to load categories",
                    type: "error",
                });
            }
        } catch (error: any) {
            console.error("Error fetching categories:", error);
        } finally {
            setLoadingCategories(false);
        }
    };

    useEffect(() => {
        getCategories();
    }, []);

    useEffect(() => {
        if (editData && Object.keys(editData).length > 0) {
            setFormData({
                question: editData.question || "",
                answer: editData.answer || "",
                categoryId: editData.category?._id || "",
                order: editData.order ?? "",
                isActive: editData.isActive ?? false,
            });
        }
    }, [editData]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.question) newErrors.question = "Question is required";
        if (!formData.answer) newErrors.answer = "Answer is required";
        if (!formData.categoryId) newErrors.categoryId = "Category is required";
        if (formData.order === "") newErrors.order = "Order is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;

        if (name === "isActive") {
            setFormData((prev: any) => ({
                ...prev,
                isActive: value === "true",
            }));
            return;
        }

        if (name === "order") {
            setFormData((prev: any) => ({
                ...prev,
                order: Number(value),
            }));
            return;
        }

        setFormData((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;

        const payload = {
            ...formData,
            order: Number(formData.order),
        };

        try {
            if (Object.keys(editData).length === 0) {
                // Create
                // console.log("faq create payload", payload)
                const response: any = await appRequest("faq", "create", payload);
                if (response.status === "success") {
                    toastNotify({
                        title: "Success",
                        message: "FAQ created successfully",
                        type: "success",
                    });
                    getFaqs();
                    setIsCreateModalOpen(false);
                } else {
                    toastNotify({
                        title: "Error",
                        message: response.message,
                        type: "error",
                    });
                }
            } else {
                // Edit
                // console.log("faq update payload", payload)
                await onSubmit({
                    ...editData,   // keep all old fields
                    ...formData,
                });
            }
        } catch (error: any) {
            console.error("Error in saving FAQ", error.message);
        }
    };

    return (
        <Box mt={4}>
            <form onSubmit={handleSubmit}>
                <Box
                    mb={8}
                    p={5}
                    shadow="base"
                    rounded="lg"
                    borderWidth={1}
                >
                    {/* Question - full width */}
                    <FormControl isInvalid={!!errors.question} mb={6}>
                        <FormLabel htmlFor="question">Question</FormLabel>
                        <Input
                            id="question"
                            name="question"
                            placeholder="Enter question"
                            value={formData.question}
                            onChange={handleInputChange}
                        />
                        <FormErrorMessage>{errors.question}</FormErrorMessage>
                    </FormControl>

                    {/* Answer - full width */}
                    <FormControl isInvalid={!!errors.answer} mb={6}>
                        <FormLabel htmlFor="answer">Answer</FormLabel>
                        <Textarea
                            id="answer"
                            name="answer"
                            placeholder="Enter answer"
                            value={formData.answer}
                            onChange={handleInputChange}
                        />
                        <FormErrorMessage>{errors.answer}</FormErrorMessage>
                    </FormControl>

                    {/* Grid for remaining fields */}
                    <Grid gap={6} templateColumns={{ base: "1fr", md: "1fr 1fr" }}>
                        {/* Category */}
                        <FormControl isInvalid={!!errors.categoryId}>
                            <FormLabel htmlFor="categoryId">Category</FormLabel>
                            <Select
                                id="categoryId"
                                name="categoryId"
                                placeholder={loadingCategories ? "Loading..." : "Select Category"}
                                value={formData.categoryId}
                                onChange={handleInputChange}
                                isDisabled={loadingCategories}
                            >
                                {categories.map((cat) => (
                                    <option key={cat._id} value={cat._id}>
                                        {cat.name}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.categoryId}</FormErrorMessage>
                        </FormControl>

                        {/* Order */}
                        <FormControl isInvalid={!!errors.order}>
                            <FormLabel htmlFor="order">Order</FormLabel>
                            <Input
                                id="order"
                                name="order"
                                type="number"
                                placeholder="Enter order"
                                value={formData.order}
                                onChange={handleInputChange}
                            />
                            <FormErrorMessage>{errors.order}</FormErrorMessage>
                        </FormControl>

                        {/* Status */}
                        <FormControl isInvalid={!!errors.isActive}>
                            <FormLabel htmlFor="isActive">Status</FormLabel>
                            <Select
                                id="isActive"
                                name="isActive"
                                value={formData.isActive ? "true" : "false"}
                                onChange={handleInputChange}
                            >
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </Select>
                            <FormErrorMessage>{errors.isActive}</FormErrorMessage>
                        </FormControl>
                    </Grid>
                </Box>

                <Flex justify="end" mt={4}>
                    <Button type="submit" colorScheme="teal">
                        {Object.keys(editData).length === 0 ? "Create" : "Update"}
                    </Button>
                </Flex>
            </form>

        </Box>
    );
}
